import styled from '@emotion/styled';
import { css } from '@emotion/core';
import React from 'react';
import {
  BREAKPOINT_SMALL,
  COLOR_DARK_PINK,
  COLOR_DARK_PURPLE,
  MarketingSection,
} from 'components/MarketingPagesShared/styles';
import {
  ListHeader,
  ListText,
  ListItem,
  Title,
  DualColumns,
  List,
} from 'components/MarketingPagesShared/DualColumnList';
import * as ThreeColumnList from 'components/MarketingPagesShared/ThreeColumnList';
import { useT } from 'client/i18n/hooks/useT';
import { CDN_PATH } from '../../constants';

const backgroundImages = css`
  background-color: ${COLOR_DARK_PINK};
  background-image: url('${CDN_PATH}/bg-red.svg'),
    url('${CDN_PATH}/bg-purple.svg');
  background-repeat: repeat-x;
  // Chrome has a pixel-rounding error so sometimes the background doesn't
  // land on the bottom, this is a workaround for that
  background-position: left top, left calc(100% + 2px);
`;

const SwitchReasonsDesktopBackground = styled.section`
  @media (min-width: ${BREAKPOINT_SMALL}px) {
    ${backgroundImages};
    padding-top: 200px;
    padding-bottom: 200px;
  }
  @media (max-width: ${BREAKPOINT_SMALL}px) {
    background: ${COLOR_DARK_PURPLE};
    padding-top: 0;
    padding-bottom: 100px;
  }
`;

const SwitchReasonsMobileBackground = styled.section`
  @media (max-width: ${BREAKPOINT_SMALL}px) {
    ${backgroundImages};
    background-size: 1000px auto;
  }
`;

export function SwitchReasonsSection() {
  const t = useT();
  return (
    <SwitchReasonsDesktopBackground>
      <SwitchReasonsMobileBackground>
        <MarketingSection>
          <DualColumns>
            <Title>
              {t(
                'ImportScreen.SwitchReasonsSection.SwitchReasonTitle',
                'Why switch?',
                'Switch to Anchor page reason title'
              )}
            </Title>
            <List>
              <ListItem>
                <ListHeader>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonInsights',
                    'Get unparalleled insights, powered by Spotify',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListHeader>
                <ListText>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonAnalytics',
                    'Improve your content using analytics from all platforms and exclusive insights like episode dropoff and listener demographics from Spotify.',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListText>
              </ListItem>
              <ListItem>
                <ListHeader>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonFreeHosting',
                    'Earn more with free hosting and monetization',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListHeader>
                <ListText>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonUnlimitedStorage',
                    'Upload and store unlimited episodes for free, and unlock sponsorships to feature audio ads—read by you—so you can earn money on your podcast.',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListText>
              </ListItem>
              <ListItem>
                <ListHeader>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonCreate',
                    'Focus more on creating, less on logistics',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListHeader>
                <ListText>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonDistribution',
                    'We can help with everything—from distribution to audience growth and monetization. Plus, get ongoing support whenever you need it.',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListText>
              </ListItem>
              <ListItem>
                <ListHeader>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonControl',
                    'You control your podcast',
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListHeader>
                <ListText>
                  {t(
                    'ImportScreen.SwitchReasonsSection.SwitchReasonNoDowntime',
                    "You own all of your content, always. During the switch, your podcast won't experience any downtime, and you'll keep all of your subscribers in the process.",
                    'Switch to Anchor page reasons to switch'
                  )}
                </ListText>
              </ListItem>
            </List>
          </DualColumns>
        </MarketingSection>
      </SwitchReasonsMobileBackground>

      <MarketingSection>
        <ThreeColumnList.Title>
          {t(
            'ImportScreen.SwitchReasonsSection.SwitchReasonNoDifference',
            "You'll notice a difference, but your listeners won't notice a thing",
            'Switch to Anchor page reasons to switch'
          )}
        </ThreeColumnList.Title>
        <ThreeColumnList.List>
          <li>
            <ThreeColumnList.Header>
              {t(
                'ImportScreen.SwitchReasonsSection.ImportEpisodesHeader',
                'Import your episodes',
                'Switch to Anchor page import episodes title'
              )}
            </ThreeColumnList.Header>
            <ThreeColumnList.SecondaryText>
              {t(
                'ImportScreen.SwitchReasonsSection.ImportEpisodesMessage',
                "Simply paste in your RSS feed, and we'll import all of your episodes and metadata—no manual uploading required.",
                'Switch to Anchor page import episodes message'
              )}
            </ThreeColumnList.SecondaryText>
          </li>
          <li>
            <ThreeColumnList.Header>
              {t(
                'ImportScreen.SwitchReasonsSection.RedirectFeedHeader',
                'Redirect your feed',
                'Switch to Anchor page redirect feed header'
              )}
            </ThreeColumnList.Header>
            <ThreeColumnList.SecondaryText>
              {t(
                'ImportScreen.SwitchReasonsSection.RedirectFeedMessage',
                'Redirecting will let your listeners hear new episodes, without affecting your subscribers on listening apps.',
                'Switch to Anchor pageredirect feed message'
              )}
            </ThreeColumnList.SecondaryText>
          </li>
          <li>
            <ThreeColumnList.Header>
              {t(
                'ImportScreen.SwitchReasonsSection.StartCreatingHeader',
                'Start creating',
                'Switch to Anchor page start creating header'
              )}
            </ThreeColumnList.Header>
            <ThreeColumnList.SecondaryText>
              {t(
                'ImportScreen.SwitchReasonsSection.StartCreatingMessage',
                'Draft new episodes by uploading audio or using our built-in creation tools.',
                'Switch to Anchor page start creating message'
              )}
            </ThreeColumnList.SecondaryText>
          </li>
        </ThreeColumnList.List>
      </MarketingSection>
    </SwitchReasonsDesktopBackground>
  );
}
